import { ArrowRightIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { BookOpenIcon, PlusIcon } from '@heroicons/react/24/outline';

import { Logo } from '@/components/Logo';
import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import { usePageContext } from '@/renderer/utils/usePageContext';

import srcOddsLogicDark from './components/oddslogic-dark.png';
import srcOddsLogicLight from './components/oddslogic-light.png';
import srcOddsLogicScreen from './components/oddslogic-screen.png';

export default OddsLogicPage;

export function OddsLogic() {
  const {
    config: { title },
  } = usePageContext();

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header
        title="Join OddsLogic and get $250 RAS credit"
        Icon={BookOpenIcon}
        className="py-2 sm:hidden"
      />
      <div className="overflow-auto">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-2xl space-y-8 pt-20 pb-10 text-center">
            <div className="space-y-8 rounded bg-gradient-to-br from-emerald-500/20 via-cyan-400/10 to-emerald-500/20 p-4 sm:p-8 dark:bg-white/5">
              <div className="mx-auto flex w-fit space-x-4">
                <Logo className="size-12 select-none" />
                <PlusIcon
                  className="h-auto w-6 shrink-0 text-zinc-500 dark:text-zinc-400"
                  aria-hidden="true"
                />
                <img
                  src={srcOddsLogicLight}
                  alt="OddsLogic logo"
                  title="OddsLogic"
                  className="h-12 w-auto self-center dark:hidden"
                />
                <img
                  src={srcOddsLogicDark}
                  alt="OddsLogic logo"
                  title="OddsLogic"
                  className="hidden h-12 w-auto self-center dark:inline-block"
                />
              </div>
              <h1 className="font-semibold text-xl text-zinc-900 tracking-tight sm:text-2xl dark:text-zinc-200">
                {typeof title === 'string' ? title : ''}
              </h1>
              <div className="p-4 text-center">
                <h4>
                  Sign up and use the referral code “RAS” to receive a $250 credit to use toward any
                  RAS service.
                </h4>
                <a
                  href="https://www.oddslogic.com/ras/?ref=ras"
                  target="_blank"
                  rel="noreferrer"
                  className="w-full sm:w-auto"
                >
                  <li className="group mt-4 inline-flex w-full flex-row items-center justify-center space-x-0.5 overflow-hidden rounded bg-emerald-500 p-2 px-6 font-medium text-sm text-white ring-1 ring-emerald-500 ring-inset transition hover:bg-emerald-400 sm:w-auto">
                    <span>Join OddsLogic</span>
                    <ChevronRightIcon className="size-5 shrink-0 group-hover:hidden" />
                    <ArrowRightIcon className="hidden size-5 p-px transition-transform group-hover:block" />
                  </li>
                </a>
              </div>
            </div>
            {/* <hr className="h-px border-0 bg-zinc-900/10 dark:bg-white/10" /> */}
            <div className="text-left">
              <h4 className="mb-8">Offer terms & conditions</h4>
              <ul className="list-disc space-y-2 pl-4 text-xs text-zinc-500 dark:text-zinc-400">
                <li>New OddsLogic subscribers only.</li>
                <li>RAS's referral link or promo code must be used.</li>
                <li>
                  Subscribe to OddsLogic for at least 1 month for $99 to be eligible. Offer not
                  active during 7-day free trial.
                </li>
                <li>
                  Contact RAS on{' '}
                  <a href="/support-chat" className="text-zinc-500" target="_self">
                    Support Chat
                  </a>
                  . You will be asked to forward us your OddsLogic subscription confirmation email.
                </li>
                <li>
                  RAS will add $250 to your account balance which will automatically apply toward
                  your next RAS order. Promotional RAS balance is not redeemable for cash.
                </li>
              </ul>
            </div>
            <hr className="h-px border-0 bg-zinc-900/10 dark:bg-white/10" />
          </div>
        </div>

        <div className="relative mt-8 px-4 sm:mt-0 sm:px-6 lg:px-8">
          <div className="prose prose-sm prose-zinc dark:prose-invert mx-auto max-w-prose prose-a:font-semibold prose-a:text-emerald-500 hover:prose-a:text-emerald-600">
            <img
              src={srcOddsLogicScreen}
              alt="OddsLogic odds screen"
              title="OddsLogic odds screen"
              className="w-full self-center shadow-lg ring-1 ring-zinc-200"
            />
            <h3 className="mb-8">
              OddsLogic is a real-time odds provider with breaking injury and line alerts
            </h3>
            <ul>
              <li>Includes top sportsbooks</li>
              <li>Breaking injury alerts</li>
              <li>Historical data</li>
              <li>Line movement history</li>
              <li>And more...</li>
            </ul>
            <hr className="h-px border-0 bg-zinc-900/10 dark:bg-white/10" />
            <h3 className="mb-8">Why OddsLogic was created</h3>
            <p>
              After their successful exit from DonBest, the OddsLogic team saw a significant gap in
              the market. Despite growing demand for real-time sports betting data, no platform
              truly met the needs of modern bettors. That’s why they built OddsLogic — to offer a
              platform that delivers accurate, real-time information, while being flexible enough to
              evolve with the industry’s changing demands.
            </p>
            <hr className="h-px border-0 bg-zinc-900/10 dark:bg-white/10" />
            <h3 className="mb-8">5 reasons to choose OddsLogic</h3>
            <ol>
              <li>
                <h5 className="font-bold text-zinc-600 dark:text-zinc-200">
                  Lightning-fast, accurate data
                </h5>
                <p>
                  OddsLogic provides the most up-to-date and reliable odds, injury reports, and key
                  data for hundreds of leagues and sports, ensuring you never miss a beat.
                </p>
              </li>
              <li>
                <h5 className="font-bold text-zinc-600 dark:text-zinc-200">
                  Comprehensive coverage
                </h5>
                <p>
                  With live odds feeds from over 38 sportsbooks, OddsLogic covers all regulated
                  betting apps, key offshore sportsbooks, and per-head shops.
                </p>
              </li>
              <li>
                <h5 className="font-bold text-zinc-600 dark:text-zinc-200">Mobile friendly</h5>
                <p>
                  Access OddsLogic on any mobile browser. Track real-time odds and line movements
                  effortlessly from your phone, wherever you are.
                </p>
              </li>
              <li>
                <h5 className="font-bold text-zinc-600 dark:text-zinc-200">
                  Real-time notifications
                </h5>
                <p>
                  Stay ahead of the game with instant alerts for breaking injuries, pitching
                  changes, halftime scores, final results, and more. Tailor your notifications by
                  type and league to suit your needs.
                </p>
              </li>
              <li>
                <h5 className="font-bold text-zinc-600 dark:text-zinc-200">API access</h5>
                <p>
                  For seamless integration, the OddsLogic API gives you easy access to all the data
                  you need. Their fully managed solution handles sourcing, updating, and verifying
                  data 24/7.
                </p>
              </li>
            </ol>
            <hr className="h-px border-0 bg-zinc-900/10 dark:bg-white/10" />
            <div className="text-center">
              <h4>
                Sign up and use the referral code “RAS” to receive a $250 credit to use toward any
                RAS service.
              </h4>
              <a
                href="https://www.oddslogic.com/ras/?ref=ras"
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-auto"
              >
                <li className="group mt-4 inline-flex w-full flex-row items-center justify-center space-x-0.5 overflow-hidden rounded bg-emerald-500 p-2 px-6 font-medium text-sm text-white ring-1 ring-emerald-500 ring-inset transition hover:bg-emerald-400 sm:w-auto">
                  <span>Join OddsLogic</span>
                  <ChevronRightIcon className="size-5 shrink-0 group-hover:hidden" />
                  <ArrowRightIcon className="hidden size-5 p-px transition-transform group-hover:block" />
                </li>
              </a>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

// Main component
function OddsLogicPage() {
  return <OddsLogic />;
}
